import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search, MapPin, ChevronLeft, ChevronRight,
  Crosshair, Mic, ChevronDown, TrendingUp, Loader2, Landmark,
} from "lucide-react";
import api from "../../lib/api";
import { useApp } from "../../context/AppContext";
import { formatINR } from "../../lib/format";
import HeroSlide from "./HeroSlide";

const TABS = [
  { id: "buy", label: "Buy", listing: "buy", category: "" },
  { id: "rent", label: "Rent", listing: "rent", category: "" },
  { id: "new", label: "New Launch", listing: "", category: "", flag: "new_launch" },
  { id: "commercial", label: "Commercial", listing: "", category: "commercial" },
  { id: "plots", label: "Plots/Land", listing: "", category: "plots" },
];

const TYPES = ["All Residential", "Apartment", "Villa", "Plot", "Office Space", "Shop"];

const fromProperty = (p) => ({
  id: null, key: p.slug, layout: "split", panel_style: "white", image: p.image, alt: p.name, focal_x: 50, focal_y: 50,
  badge: `${p.property_type} · ${p.bedrooms > 0 ? `${p.bedrooms} BHK` : "Premium"}`, title: p.name,
  offer_text: `Starting @ ${p.listing_type === "rent" ? `${formatINR(p.price)}/mo` : formatINR(p.price)} onwards`,
  description: `${p.locality}, ${p.city}`, primary_label: "Explore Now", primary_url: `/property/${p.slug}`,
});
const reducedMotion = () => typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

export function Hero() {
  const navigate = useNavigate();
  const { addRecentSearch } = useApp();
  const [slides, setSlides] = useState(null);
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const [tab, setTab] = useState("buy");
  const [type, setType] = useState("All Residential");
  const [nl, setNl] = useState("");
  const [parsing, setParsing] = useState(false);
  const seen = useRef(new Set());
  const touchX = useRef(null);

  useEffect(() => {
    api.heroCampaigns().then(({ items }) => {
      if (items.length) return setSlides(items);
      return api.properties({ new_launch: true, limit: 5 }).then((d) => setSlides(d.map(fromProperty)));
    }).catch(() => setSlides([]));
  }, []);

  const count = slides?.length || 0;
  const next = useCallback(() => setIdx((i) => (i + 1) % Math.max(count, 1)), [count]);
  const prev = useCallback(() => setIdx((i) => (i - 1 + Math.max(count, 1)) % Math.max(count, 1)), [count]);

  useEffect(() => {
    if (count < 2 || paused || reducedMotion()) return;
    const t = setInterval(next, 7000);
    return () => clearInterval(t);
  }, [count, paused, next]);

  const active = slides?.[idx];
  useEffect(() => {
    if (!active?.id || seen.current.has(active.id)) return;
    seen.current.add(active.id);
    const k = `hc_seen_${active.id}`;
    if (sessionStorage.getItem(k)) return;
    sessionStorage.setItem(k, "1");
    api.heroEvent(active.id, "impression");
  }, [active]);

  const baseParams = () => {
    const t = TABS.find((x) => x.id === tab);
    const params = new URLSearchParams();
    if (t.listing) params.set("listing_type", t.listing);
    if (t.category) params.set("category", t.category);
    if (t.flag) params.set("new_launch", "true");
    if (type && type !== "All Residential") params.set("property_type", type);
    return params;
  };

  const doSearch = async (e) => {
    e?.preventDefault();
    const q = nl.trim();
    if (!q) {
      const t = TABS.find((x) => x.id === tab);
      addRecentSearch(`${type !== "All Residential" ? type : "Property"} · ${t.label}`);
      navigate(`/properties?${baseParams().toString()}`);
      return;
    }
    setParsing(true);
    try {
      const { filters } = await api.parseSearch(q);
      const params = baseParams();
      Object.entries(filters || {}).forEach(([k, v]) => { if (v !== "" && v != null && k !== "q") params.set(k, v); });
      addRecentSearch(q);
      navigate(`/properties?${params.toString()}`);
    } catch { navigate(`/properties?q=${encodeURIComponent(q)}`); }
    finally { setParsing(false); }
  };

  return (
    <section className="relative bg-white">
      <div className="relative overflow-hidden border-b border-pbs-line" data-testid="hero-campaigns" aria-roledescription="carousel"
        onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} onFocus={() => setPaused(true)} onBlur={() => setPaused(false)}
        onTouchStart={(e) => { touchX.current = e.touches[0].clientX; }} onTouchEnd={(e) => { if (touchX.current == null) return; const dx = e.changedTouches[0].clientX - touchX.current; touchX.current = null; if (Math.abs(dx) > 48) (dx < 0 ? next : prev)(); }}>
        {slides === null ? <div className="h-[460px] md:h-[400px] lg:h-[440px] bg-[#F7FAF7] animate-pulse" data-testid="hero-loading" />
          : !active ? (
            <div className="h-[300px] md:h-[400px] lg:h-[440px] bg-white flex items-center" data-testid="hero-empty">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full pb-10"><p className="text-[11px] font-extrabold tracking-[0.3em] text-pbs-orange">REAL ESTATE. HIGHER POSSIBILITIES.</p><h1 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-pbs-dark tracking-tight">Find your next property</h1></div>
            </div>
          ) : (
            <div key={active.id || active.key} className="animate-fade-in motion-reduce:animate-none" data-testid={`hero-slide-${idx}`} role="group" aria-label={`Slide ${idx + 1} of ${count}`}>
              <HeroSlide c={active} onClick={(b) => active.id && api.heroEvent(active.id, "click", b)} />
            </div>
          )}

        {count > 1 && (
          <>
            <button onClick={prev} data-testid="hero-prev" aria-label="Previous campaign"
              className="hidden md:flex absolute left-4 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white border border-[#E5EAE6] shadow-sm text-[#236B45] items-center justify-center hover:border-[#236B45]/50 transition-colors z-10">
              <ChevronLeft size={18} />
            </button>
            <button onClick={next} data-testid="hero-next" aria-label="Next campaign"
              className="hidden md:flex absolute right-4 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white border border-[#E5EAE6] shadow-sm text-[#236B45] items-center justify-center hover:border-[#236B45]/50 transition-colors z-10">
              <ChevronRight size={18} />
            </button>
            <div className="absolute left-1/2 -translate-x-1/2 md:translate-x-0 md:left-6 bottom-3 md:bottom-14 flex gap-1.5 z-10" data-testid="hero-dots">
              {slides.map((s, i) => (
                <button key={s.id || s.key} onClick={() => setIdx(i)} aria-label={`Go to campaign ${i + 1}`} aria-current={i === idx}
                  className={`h-2 rounded-full transition-all ${i === idx ? "w-6 bg-[#236B45]" : "w-2 bg-[#C3CFC6] hover:bg-[#93A69A]"}`} />
              ))}
            </div>
          </>
        )}
      </div>

      {/* Floating light search card — 99acres style */}
      <div className="relative z-20 max-w-4xl mx-auto px-4 sm:px-6 -mt-10">
        <div className="bg-white rounded-xl shadow-[0_12px_36px_rgba(32,32,32,0.14)] border border-pbs-line">
          {/* Tabs row */}
          <div className="flex items-center overflow-x-auto no-scrollbar border-b border-pbs-line px-1">
            {TABS.map((t) => (
              <button key={t.id} data-testid={`search-tab-${t.id}`} onClick={() => setTab(t.id)}
                className={`relative px-4 sm:px-5 py-3 text-sm font-bold whitespace-nowrap transition-colors ${tab === t.id ? "text-pbs-purple" : "text-pbs-grey hover:text-pbs-dark"}`}>
                {t.label}
                {tab === t.id && <span className="absolute left-3 right-3 bottom-0 h-[3px] bg-pbs-purple rounded-t-full" />}
              </button>
            ))}
            <div className="ml-auto hidden sm:block pr-3">
              <button onClick={() => navigate("/post-property")} data-testid="hero-post-property"
                className="flex items-center gap-1.5 text-sm font-bold text-pbs-dark whitespace-nowrap">
                Post Property <span className="bg-green-600 text-white text-[9px] font-extrabold px-1.5 py-0.5 rounded">FREE</span>
              </button>
            </div>
          </div>

          {/* Single fields row */}
          <form onSubmit={doSearch} className="flex flex-col sm:flex-row sm:items-center divide-y sm:divide-y-0 sm:divide-x divide-pbs-line">
            <div className="relative sm:w-44 shrink-0">
              <select data-testid="search-type" value={type} onChange={(e) => setType(e.target.value)}
                className="w-full appearance-none bg-white px-4 py-3.5 text-sm font-semibold text-pbs-dark outline-none cursor-pointer pr-8 sm:rounded-bl-xl">
                {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
              <ChevronDown size={15} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-pbs-grey pointer-events-none" />
            </div>
            <div className="flex-1 flex items-center gap-2 px-4 bg-white">
              <Search size={17} className="text-pbs-grey shrink-0" />
              <input data-testid="hero-nl-search" value={nl} onChange={(e) => setNl(e.target.value)}
                placeholder='Search e.g. "3 BHK in Noida under ₹2 Cr"'
                className="flex-1 bg-transparent outline-none text-sm py-3.5 text-pbs-dark placeholder:text-pbs-grey/70 min-w-0" />
              <button type="button" aria-label="Near me" className="p-1.5 text-pbs-purple hover:bg-pbs-purple/10 rounded-full transition-colors"><Crosshair size={17} /></button>
              <button type="button" aria-label="Voice search" className="p-1.5 text-pbs-purple hover:bg-pbs-purple/10 rounded-full transition-colors"><Mic size={17} /></button>
            </div>
            <div className="sm:pl-3 p-3 sm:p-0 sm:pr-3">
              <button type="submit" data-testid="hero-search-btn" disabled={parsing}
                className="w-full sm:w-auto flex items-center justify-center gap-2 bg-pbs-purple hover:bg-pbs-plum text-white font-bold rounded-lg px-6 py-3 transition-colors">
                {parsing ? <Loader2 size={17} className="animate-spin" /> : <Search size={17} />} Search
              </button>
            </div>
          </form>
        </div>

        {/* Continue browsing */}
        <div className="mt-4 flex items-center gap-2.5 overflow-x-auto no-scrollbar" data-testid="continue-browsing">
          <span className="text-xs font-semibold text-pbs-grey whitespace-nowrap">Continue browsing…</span>
          {["Noida", "Greater Noida", "Noida Extension"].map((c) => (
            <button key={c} onClick={() => navigate(`/city/${c.toLowerCase().replace(/\s/g, "-")}`)}
              className="flex items-center gap-1.5 bg-white border border-pbs-line rounded-full px-3.5 py-2 text-xs font-semibold text-pbs-dark whitespace-nowrap hover:border-pbs-purple hover:text-pbs-purple transition-colors">
              <Landmark size={13} className="text-pbs-purple" /> Buy in {c}
            </button>
          ))}
          <button onClick={() => navigate("/properties")} className="flex items-center gap-1.5 bg-white border border-pbs-line rounded-full px-3.5 py-2 text-xs font-semibold text-pbs-dark whitespace-nowrap hover:border-pbs-purple hover:text-pbs-purple transition-colors">
            <MapPin size={13} className="text-pbs-purple" /> Explore All
          </button>
          <button data-testid="explore-investment-cta" onClick={() => navigate("/invest")}
            className="flex items-center gap-1.5 text-pbs-purple font-semibold text-xs bg-pbs-purple/5 border border-pbs-purple/20 px-3.5 py-2 rounded-full whitespace-nowrap hover:bg-pbs-purple/10 transition-colors">
            <TrendingUp size={13} className="text-pbs-orange" /> Explore Investment Opportunities
          </button>
        </div>
      </div>
    </section>
  );
}

export default Hero;
